import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';
import '../styles/global.css';

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notifications, setNotifications] = useState({ email: true, sms: true, family: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { client } = useAuth();
  const navigate = useNavigate();

  const token = new URLSearchParams(window.location.search).get('token');

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/set-password', { token, password });
      setStep(3);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifications = async () => {
    try {
      await api.put('/account', {
        email_notifications: notifications.email,
        sms_notifications: notifications.sms,
        family_sharing: notifications.family
      });
    } catch {}
    setStep(4);
  };

  const dots = [1,2,3,4,5];

  return (
    <div style={{
      background: 'var(--teal)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{ marginBottom: '28px', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '24px', color: 'white' }}>
        BUMP <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span> BABY IMAGING
      </div>

      <div className="card" style={{ maxWidth: '480px', width: '100%' }}>
        {/* Step dots */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '32px' }}>
          {dots.map(d => (
            <div key={d} style={{
              width: d === step ? '28px' : '10px',
              height: '10px',
              borderRadius: '100px',
              background: d < step ? 'var(--teal)' : d === step ? 'var(--coral)' : 'var(--border)',
              transition: 'all 0.3s'
            }} />
          ))}
        </div>

        {/* STEP 1 — Welcome */}
        {step === 1 && (
          <>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal-dark)', marginBottom: '8px', textAlign: 'center' }}>
              Thanks for visiting us at Bump Baby Imaging!
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', textAlign: 'center', lineHeight: '1.6', marginBottom: '24px' }}>
              Your session memories are waiting for you.
            </p>
            {client && (
              <div style={{ background: 'var(--teal-light)', borderRadius: '14px', padding: '18px', marginBottom: '24px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-soft)', marginBottom: '12px', fontFamily: 'Nunito, sans-serif' }}>Your Session</div>
                <div style={{ fontSize: '14px', color: 'var(--text)', display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-soft)', fontWeight: 600 }}>Name</span>
                  <span style={{ fontWeight: 700 }}>{client.firstName} {client.lastName}</span>
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text)', display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-soft)', fontWeight: 600 }}>Email</span>
                  <span style={{ fontWeight: 700 }}>{client.email}</span>
                </div>
              </div>
            )}
            <button className="btn-primary" onClick={() => setStep(2)}>Get Started</button>
          </>
        )}

        {/* STEP 2 — Set Password */}
        {step === 2 && (
          <>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '8px', textAlign: 'center' }}>Create your password</h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', textAlign: 'center', lineHeight: '1.6', marginBottom: '24px' }}>
              Set a password so you can log in anytime to view your gallery.
            </p>
            {error && <p style={{ color: 'var(--coral-dark)', fontSize: '13px', marginBottom: '16px', background: 'var(--coral-light)', padding: '10px 14px', borderRadius: '8px' }}>{error}</p>}
            <form onSubmit={handleSetPassword}>
              <div className="form-group">
                <label>Your Email</label>
                <input type="email" value={client?.email || ''} readOnly style={{ background: 'var(--teal-light)', color: 'var(--text-soft)' }} />
              </div>
              <div className="form-group">
                <label>Create Password</label>
                <input type="password" placeholder="At least 8 characters" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input type="password" placeholder="Repeat your password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
              </div>
              <button className="btn-primary" type="submit" disabled={loading}>{loading ? 'Saving...' : 'Continue'}</button>
            </form>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0', color: 'var(--text-soft)', fontSize: '12px' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />or<div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            </div>
            <button className="btn-secondary" onClick={() => setStep(3)}>Send me a magic link instead</button>
          </>
        )}

        {/* STEP 3 — Notifications */}
        {step === 3 && (
          <>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '8px', textAlign: 'center' }}>Stay in the loop</h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', textAlign: 'center', lineHeight: '1.6', marginBottom: '24px' }}>
              Choose how you'd like to be notified when new session files are ready.
            </p>
            {[
              { key: 'email', title: 'Email notifications', desc: 'Get an email when your gallery is updated' },
              { key: 'sms', title: 'SMS notifications', desc: 'Get a text message when files are ready' },
              { key: 'family', title: 'Family sharing', desc: 'Allow family members to be invited to your gallery' }
            ].map(item => (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--teal-light)' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--teal-dark)', fontFamily: 'Nunito, sans-serif' }}>{item.title}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '2px' }}>{item.desc}</div>
                </div>
                <div
                  onClick={() => setNotifications(n => ({ ...n, [item.key]: !n[item.key] }))}
                  style={{
                    width: '44px', height: '24px',
                    background: notifications[item.key] ? 'var(--teal)' : 'var(--border)',
                    borderRadius: '100px',
                    position: 'relative',
                    cursor: 'pointer',
                    marginLeft: '16px',
                    transition: 'background 0.2s',
                    flexShrink: 0
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    width: '18px', height: '18px',
                    background: 'white',
                    borderRadius: '50%',
                    top: '3px',
                    left: notifications[item.key] ? 'auto' : '3px',
                    right: notifications[item.key] ? '3px' : 'auto',
                    transition: 'all 0.2s'
                  }} />
                </div>
              </div>
            ))}
            <button className="btn-primary" style={{ marginTop: '24px' }} onClick={handleNotifications}>Continue</button>
            <p style={{ textAlign: 'center', marginTop: '14px', fontSize: '13px', color: 'var(--text-soft)' }}>
              <span style={{ color: 'var(--teal)', cursor: 'pointer', fontWeight: 700 }} onClick={() => setStep(4)}>Skip for now</span>
            </p>
          </>
        )}

        {/* STEP 4 — All done */}
        {step === 4 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ width: '80px', height: '80px', background: 'var(--teal-light)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '3px solid var(--teal)' }}>
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <path d="M6 18L14 26L30 10" stroke="#539390" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '8px' }}>You're all set!</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-soft)', lineHeight: '1.6' }}>Your Bump Baby Imaging account is ready.</p>
            </div>
            {[
              'View your session video and images anytime',
              'Download your files to keep them forever',
              'Invite family to view your gallery',
              'Files stored for 12 months — download to keep forever',
              'Access all future sessions in one place'
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--teal-light)', borderRadius: '12px', fontSize: '14px', color: 'var(--teal-dark)', fontWeight: 600, marginBottom: '10px' }}>
                <div style={{ width: '8px', height: '8px', background: 'var(--coral)', borderRadius: '50%', flexShrink: 0 }} />
                {item}
              </div>
            ))}
            <button className="btn-primary" style={{ marginTop: '8px' }} onClick={() => setStep(5)}>One more thing...</button>
          </>
        )}

        {/* STEP 5 — Add to Home Screen */}
        {step === 5 && (
          <>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '8px', textAlign: 'center' }}>Add us to your home screen</h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', textAlign: 'center', lineHeight: '1.6', marginBottom: '24px' }}>
              Access your gallery anytime just like an app — no App Store needed!
            </p>
            <div style={{ background: 'var(--teal-light)', borderRadius: '14px', padding: '18px 20px', marginBottom: '14px', border: '1px solid var(--border)' }}>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '13px', color: 'var(--teal-dark)', marginBottom: '12px' }}>On iPhone (Safari)</div>
              {['Tap the Share button at the bottom of Safari', 'Scroll down and tap Add to Home Screen', 'Tap Add — done!'].map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: 'var(--text)', marginBottom: '10px' }}>
                  <div style={{ width: '24px', height: '24px', background: 'var(--teal)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: '11px', flexShrink: 0 }}>{i+1}</div>
                  {s}
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--coral-light)', borderRadius: '14px', padding: '18px 20px', marginBottom: '24px', border: '1px solid #f5c4b5' }}>
              <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '13px', color: 'var(--coral-dark)', marginBottom: '12px' }}>On Android (Chrome)</div>
              {['Tap the three dots menu at the top right', 'Tap Add to Home Screen', 'Tap Add — done!'].map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: 'var(--text)', marginBottom: '10px' }}>
                  <div style={{ width: '24px', height: '24px', background: 'var(--coral)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: '11px', flexShrink: 0 }}>{i+1}</div>
                  {s}
                </div>
              ))}
            </div>
            <button className="btn-primary" onClick={() => navigate('/')}>View My Gallery</button>
            <p style={{ textAlign: 'center', marginTop: '14px', fontSize: '13px', color: 'var(--text-soft)' }}>
              <span style={{ color: 'var(--teal)', cursor: 'pointer', fontWeight: 700 }} onClick={() => navigate('/')}>Skip for now</span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
